import { Component } from '../Component';

/**
 * AI states for beastie behavior
 */
export type AIState =
  | 'idle'       // Standing still, looking around
  | 'following'  // Following owner/target
  | 'patrol'     // Moving along patrol path
  | 'combat'     // Engaged in battle
  | 'fleeing'    // Running away
  | 'taming'     // Being tamed by player
  | 'dead';      // Defeated

/**
 * Targeting priority options
 */
export type TargetingPriority =
  | 'nearest'        // Attack closest enemy
  | 'lowest_health'  // Attack weakest enemy
  | 'highest_threat' // Attack most dangerous enemy
  | 'same_as_owner'  // Attack what owner is targeting
  | 'defensive';     // Only attack if attacked first

/**
 * AIComponent - Beastie AI state and behavior
 */
export class AIComponent extends Component {
  public readonly type = 'ai';

  /** Current AI state */
  public state: AIState;

  /** Previous state (for state transitions) */
  public previousState: AIState;

  /** Behavior tree ID to use */
  public behaviorTreeId: string;

  /** Current target entity ID */
  public targetId: string | null;

  /** How to select targets */
  public targetingPriority: TargetingPriority;

  /** Aggression level (0-1), affects engagement range and behavior */
  public aggressionLevel: number;

  /** Home position (where to return when idle) */
  public homePosition: { x: number; y: number } | null;

  /** Patrol path waypoints */
  public patrolPath: { x: number; y: number }[];

  /** Current patrol waypoint index */
  public patrolIndex: number;

  /** Maximum distance from home/owner before returning */
  public leashRange: number;

  /** Detection range for enemies */
  public detectionRange: number;

  /** Attack range */
  public attackRange: number;

  /** Time until next decision (for throttling AI updates) */
  public decisionCooldown: number;

  /** Whether AI is active */
  public isActive: boolean;

  /** Owner entity ID (for party beasties) */
  public ownerEntityId: string | null;

  constructor(data?: Partial<AIComponent>) {
    super();
    this.state = data?.state ?? 'idle';
    this.previousState = data?.previousState ?? 'idle';
    this.behaviorTreeId = data?.behaviorTreeId ?? 'default';
    this.targetId = data?.targetId ?? null;
    this.targetingPriority = data?.targetingPriority ?? 'nearest';
    this.aggressionLevel = data?.aggressionLevel ?? 0.5;
    this.homePosition = data?.homePosition ?? null;
    this.patrolPath = data?.patrolPath ?? [];
    this.patrolIndex = data?.patrolIndex ?? 0;
    this.leashRange = data?.leashRange ?? 500;
    this.detectionRange = data?.detectionRange ?? 200;
    this.attackRange = data?.attackRange ?? 50;
    this.decisionCooldown = data?.decisionCooldown ?? 0;
    this.isActive = data?.isActive ?? true;
    this.ownerEntityId = data?.ownerEntityId ?? null;
  }

  /**
   * Transition to a new state
   */
  public setState(newState: AIState): void {
    if (newState !== this.state) {
      this.previousState = this.state;
      this.state = newState;
    }
  }

  /**
   * Set the current target
   */
  public setTarget(entityId: string | null): void {
    this.targetId = entityId;
  }

  /**
   * Clear the current target
   */
  public clearTarget(): void {
    this.targetId = null;
  }

  /**
   * Check if AI can make a decision (cooldown elapsed)
   */
  public canDecide(currentTime: number): boolean {
    return currentTime >= this.decisionCooldown;
  }

  /**
   * Set decision cooldown
   */
  public setDecisionCooldown(duration: number, currentTime: number): void {
    this.decisionCooldown = currentTime + duration;
  }

  /**
   * Get next patrol waypoint
   */
  public getNextPatrolPoint(): { x: number; y: number } | null {
    if (this.patrolPath.length === 0) return null;
    return this.patrolPath[this.patrolIndex];
  }

  /**
   * Advance to next patrol waypoint
   */
  public advancePatrol(): void {
    if (this.patrolPath.length > 0) {
      this.patrolIndex = (this.patrolIndex + 1) % this.patrolPath.length;
    }
  }

  /**
   * Check if entity should flee based on health
   */
  public shouldFlee(healthPercent: number): boolean {
    // Flee threshold based on aggression (high aggression = fight longer)
    const fleeThreshold = 0.2 * (1 - this.aggressionLevel);
    return healthPercent <= fleeThreshold;
  }
}
